import { useContext, useState, forwardRef, useMemo } from 'react';
import { ACCESS_CONTROL_CONSTANTS } from '../../../../core/constants';
import useStyles from './styles';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context';
import VideoSectionSplitCard from './VideoSectionSplitCard';
import SwitchInput from '../../../../Components/SwitchInput';
import { toast } from 'react-toastify';
import DropDownSingle from '../../../../Components/DropDownSingle';
//mui imports====
import { Grid, Paper } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Divider } from '@mui/material';
import { FvpConfigContext } from '../../../../Contexts/FvpConfigContext';
import GeneralButton from '../../../../Components/GeneralButton';
import SplitVisualizerIcon from '../../../../Components/PieChartVisualizer';
import useCommonStyles from '../../../../commonStyles';
import PageHeading from '../../../../Components/PageHeading';
import { Box } from '@mui/system';
const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

/**
 * @name VideoSectionsSplitConfig
 *
 * @description This component is an editor used to manage the sectionLevelSplit configuration at the site level.
 *
 * "sectionLevelSplit": {
 *   "enabled": false,
 *   "splits": {
 *     "1": {
 *       "splitPercentage": 100
 *     }
 *   }
 * }
 *
 * @param {{keyData: 'sectionLevelSplit'}} keyData - A key used to identify the configuration within the fvpConfig object.
 */

const VideoSectionsSplitConfig = ({ keyData }) => {
	const classes = useStyles();
	const { fvpConfig, updateFvpConfig } = useContext(FvpConfigContext);
	const { updateModalContent } = useContext(Modalv2Context);
	const videoSections = fvpConfig.ads;
	const { sectionLevelSplit } = fvpConfig;
	const sectionSplits = sectionLevelSplit?.splits || [];
	const commonStyleClasses = useCommonStyles();
	/**
	 * @name deleteSectionFromSplitConfig
	 *
	 * @description Deletes a specific split configuration in the sectionLevelSplit property of the fvpConfig object.
	 *
	 * @param {string} sectionId - The key of the split configuration to delete.
	 */
	const deleteSectionFromSplitConfig = (sectionId) => {
		updateFvpConfig((fvpConfig) => {
			const clonedFvpConfig = structuredClone(fvpConfig);
			delete clonedFvpConfig.sectionLevelSplit.splits[sectionId];
			return clonedFvpConfig;
		});
	};

	/**
	 * @name updateSplitPercentage
	 *
	 * @description Updates the splitPercentage of a specific split configuration in the sectionLevelSplit property of the fvpConfig object.
	 *
	 * @param {string} sectionId - The key of the split configuration to update.
	 *
	 * @param {any} newPercentage - The new percentage value to assign to the splitPercentage property of the specified split.
	 */
	const updateSplitPercentage = (sectionId, newPercentage) => {
		const clonedFvpConfig = structuredClone(fvpConfig);
		const videoSectionSplit = clonedFvpConfig.sectionLevelSplit.splits[sectionId];

		if (!videoSectionSplit) {
			return toast.error('Section Id Not Found in Splits!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}

		videoSectionSplit.splitPercentage = newPercentage;
		updateFvpConfig(clonedFvpConfig);
	};

	/**
	 * @name handleSectionLevelSplitEnabled
	 *
	 * @description this function handle the enable/disable property of sectionLevelSplit in fvpConfig object
	 *
	 * @param {boolean} value - The new value to assign to the enabled property of the sectionLevelSplit
	 *
	 */
	const handleSectionLevelSplitEnabled = (value) => {
		const clonedFvpConfig = structuredClone(fvpConfig);
		clonedFvpConfig[keyData] = clonedFvpConfig[keyData] || {};
		clonedFvpConfig[keyData].enabled = value;
		updateFvpConfig(clonedFvpConfig);
	};

	const dataForSplitVisualizer = () => {
		const data = [];
		for (let videoSectionId in sectionSplits) {
			const splitPercentage = sectionSplits[videoSectionId].splitPercentage;
			const videoSection = fvpConfig.ads[videoSectionId];

			data.push({
				id: videoSectionId,
				value: splitPercentage,
				label: videoSection.videoSectionName
			});
		}
		return data;
	};

	const splitVisualizerData = dataForSplitVisualizer();

	return (
		<>
			<Paper className={commonStyleClasses.mainPagePaperMarginStyle}>
				<PageHeading
					headingName="Split Config Settings"
					headingPath={['Dashboard', 'Fvp', 'Split Config Settings']}
					description="This page allows you to configure the split settings for the video sections."
				/>
				<Box
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						padding: '6rem 5rem 1.4rem 5rem'
					}}
				>
					<SwitchInput
						containerStyles={{ width: 'fit-content', margin: 0 }}
						system={SYSTEMS.FVP_PLAYER}
						isObjectEditor={false}
						keyData={keyData + '.enabled'}
						label={'Enabled'}
						defaultChecked={false}
						directValue={fvpConfig[keyData]?.enabled}
						updateDirectValue={(value) => handleSectionLevelSplitEnabled(value)}
					/>
					<SplitVisualizerIcon
						data={splitVisualizerData}
						sumRes={100}
						updateModalContent={updateModalContent}
					/>
				</Box>

				<Grid container spacing={2} style={{ paddingLeft: '3rem' }}>
					{sectionLevelSplit &&
					sectionLevelSplit.splits &&
					Object.keys(sectionLevelSplit.splits).length > 0
						? Object.keys(sectionLevelSplit.splits).map((videoSectionId, index) => (
								<Grid item xs={4} key={videoSectionId}>
									<VideoSectionSplitCard
										videoSections={videoSections}
										videoSectionId={videoSectionId}
										sectionSplitData={sectionLevelSplit.splits[videoSectionId]}
										keyData={keyData + `.splits.${videoSectionId}`}
										updateSplitPercentage={updateSplitPercentage}
										deleteSectionFromSplitConfig={deleteSectionFromSplitConfig}
									/>
								</Grid>
						  ))
						: null}
					<Grid
						item
						xs={12}
						style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center' }}
					>
						{(sectionLevelSplit?.splits === undefined ||
							Object.keys(sectionLevelSplit.splits).length < Object.keys(videoSections).length) && (
							<AddCircleOutlineOutlinedIcon
								className={classes.icon}
								color="primary"
								fontSize="medium"
								onClick={() => updateModalContent(<AddSectionSplit keyData={keyData} />)}
							/>
						)}
					</Grid>
				</Grid>
			</Paper>
		</>
	);
};

const AddSectionSplit = ({ keyData }) => {
	const { fvpConfig, updateFvpConfig } = useContext(FvpConfigContext);
	const { sectionLevelSplit } = fvpConfig;
	const [selectedExistingVideoSection, setSelectedExistingVideoSection] = useState('');
	const { updateModalContent } = useContext(Modalv2Context);
	const videoSections = fvpConfig.ads;
	const commonStyleClasses = useCommonStyles();

	const updateSectionLevelSplit = (newSectionLevelSplit) => {
		const clonedFvpConfig = structuredClone(fvpConfig);
		clonedFvpConfig.sectionLevelSplit = newSectionLevelSplit;
		updateFvpConfig(clonedFvpConfig);
	};

	const availableSections = useMemo(() => {
		let existingSectionIds = [];

		if (sectionLevelSplit?.splits) {
			Object.keys(sectionLevelSplit.splits).forEach((ids) => {
				existingSectionIds = [...existingSectionIds, ids];
			});
		}

		const remainingSectionIds = [];

		for (const sectionKey in videoSections) {
			const videoSectionId = videoSections[sectionKey].videoSectionId;
			const isNewSection = !existingSectionIds.includes(videoSectionId);

			if (isNewSection) {
				remainingSectionIds.push(videoSectionId);
			}
		}

		return remainingSectionIds;
	}, [sectionLevelSplit, fvpConfig.ads]);

	const addNewSplitSection = () => {
		try {
			const clonedSectionLevelSplit = structuredClone(sectionLevelSplit || {});

			clonedSectionLevelSplit.splits = clonedSectionLevelSplit.splits || {};

			const videoSectionId = videoSections[selectedExistingVideoSection].videoSectionId;
			clonedSectionLevelSplit.splits[videoSectionId] = {
				splitPercentage: 0
			};

			updateSectionLevelSplit(clonedSectionLevelSplit);
			updateModalContent(null);
		} catch (error) {
			toast.error(error.message, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
		}
	};

	return (
		<Grid
			container
			spacing={2}
			className={commonStyleClasses.modalContentStyle}
			style={{ width: 'fit-content', height: 'fit-content' }}
		>
			<Grid item xs={12}>
				<Typography className={commonStyleClasses.heading} fontWeight={600} variant="h6">
					Add New Section Split
				</Typography>
			</Grid>
			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<DropDownSingle
					system={SYSTEMS.FVP_PLAYER}
					isObjectEditor={false}
					keyData={keyData + '.splits'}
					formControlVariant="outlined"
					directValue={selectedExistingVideoSection}
					updateDirectValue={(value) => {
						setSelectedExistingVideoSection(value);
					}}
					list={availableSections?.map((videoSectionId) => {
						const adsSection = videoSections[videoSectionId];
						const sectionName = adsSection.videoSectionName;
						return {
							label: sectionName,
							value: videoSectionId
						};
					})}
				/>
			</Grid>

			<Grid item xs={12} className={commonStyleClasses.modalContentItem}>
				<GeneralButton
					system={SYSTEMS.FVP_PLAYER}
					name="ADD_VIDEO_SECTION_SPLIT_CONFIG"
					variant="contained"
					style={{ width: '10rem', height: '5rem' }}
					onClick={addNewSplitSection}
				>
					ADD
				</GeneralButton>
			</Grid>
		</Grid>
	);
};

export default VideoSectionsSplitConfig;
